import { Wallet, ArrowDownRight, ArrowUpRight, Banknote, TrendingUp, Calculator, FileBarChart2, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Inbox } from "lucide-react";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { AdminAPI } from "@/lib/api";
import { downloadCsv } from "@/lib/download";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// Label ramah untuk jenis saldo sistem.
const WALLET_LABEL = {
  KOPERASI_PUSAT: "Kas Escrow Pusat", KANTOR_WILAYAH: "Kas Kantor Wilayah",
  KANTOR_DAERAH: "Kas Kantor Daerah",
  KANTOR_CABANG: "Kas Kantor Cabang", KANTOR_RANTING: "Kas Kantor Ranting",
  UNIT_GERAI: "Kas Unit Gerai",
};

export default function AdminFinance() {
  const navigate = useNavigate();
  // Ledger nyata dari tabel transactions.
  const [rows, setRows] = useState([]);
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  useEffect(() => {
    AdminAPI.transactions().then((data) => {
      setRows((data || []).map((t) => ({
        id: t.trx_code,
        date: t.created_at,
        desc: t.reference || `${t.type} · ${t.wallet_type}`,
        category: t.direction === "CREDIT" ? "Penerimaan" : "Pengeluaran",
        amount: t.direction === "CREDIT" ? Number(t.amount) : -Number(t.amount),
      })));
    }).catch(() => {}).finally(() => setLedgerLoaded(true));
  }, []);

  // Arus kas mingguan + saldo sistem (dompet & kas wilayah) dari backend.
  const [cashFlow, setCashFlow] = useState([]);
  const [balances, setBalances] = useState(null);
  const [flowLoaded, setFlowLoaded] = useState(false);
  useEffect(() => {
    AdminAPI.cashflow().then((d) => {
      setCashFlow(d.weekly || []);
      setBalances(d);
    }).catch(() => {}).finally(() => setFlowLoaded(true));
  }, []);

  // Ringkasan KPI dari saldo & arus kas nyata.
  const kasTersedia = balances
    ? balances.wallets.buyer + balances.wallets.seller + balances.wallets.affiliate + (balances.kas || []).reduce((s, k) => s + Number(k.saldo), 0)
    : null;
  const hasCashFlow = cashFlow.some((c) => c.masuk > 0 || c.keluar > 0);

  // Unduh buku besar (ledger) sesuai data nyata.
  const exportLedger = () => {
    if (rows.length === 0) return toast.info("Belum ada transaksi untuk diunduh");
    downloadCsv("buku-besar", ["ID", "Tanggal", "Deskripsi", "Kategori", "Nominal"],
      rows.map((t) => [t.id, formatDate(t.date), t.desc, t.category, t.amount]));
    toast.success(`Buku besar diunduh (${rows.length} baris)`);
  };

  // Unduh audit trail keuangan.
  const exportAudit = async () => {
    try {
      const logs = await AdminAPI.auditLogs();
      if (!logs?.length) return toast.info("Belum ada log audit");
      downloadCsv("audit-keuangan", ["Waktu", "Pelaku", "Peran", "Aksi", "IP"],
        logs.map((l) => [new Date(l.created_at).toLocaleString("id-ID"), l.actor_name || "Sistem", l.actor_role || "", l.action_type, l.ip_address || ""]));
      toast.success("Laporan audit diunduh");
    } catch { toast.error("Gagal mengunduh audit"); }
  };

  // Susun daftar saldo untuk panel "rekonsiliasi" dari data nyata.
  const accountList = balances ? [
    { bank: "Dompet Belanja Anggota", saldo: balances.wallets.buyer, sync: "Real-time", color: "bg-sage/20 text-forest" },
    { bank: "Dompet Penjual (Seller)", saldo: balances.wallets.seller, sync: "Real-time", color: "bg-sage/20 text-forest" },
    { bank: "Dompet Afiliasi", saldo: balances.wallets.affiliate, sync: "Real-time", color: "bg-amber-warm/20 text-amber-700" },
    ...(balances.kas || []).map((k) => ({ bank: WALLET_LABEL[k.wallet_type] || k.wallet_type, saldo: k.saldo, sync: "Ledger", color: "bg-stone-soft/40 text-ink/75" })),
  ] : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Kas & Keuangan</h1>
          <p className="text-ink/75 mt-2 text-sm">Buku besar, simpanan, SHU, dan rekonsiliasi bank koperasi.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-lg border-stone-soft" onClick={exportLedger} data-testid="finance-export"><Download size={15} /> Unduh Buku Besar</Button>
          <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => navigate("/admin/shu")} data-testid="finance-shu-calc"><Calculator size={15} /> Setting SHU</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <FinanceCard label="Kas & Saldo Sistem" value={kasTersedia != null ? formatRupiah(kasTersedia) : "—"} icon={Wallet} sub="Dompet + kas wilayah" />
        <FinanceCard label="Penerimaan 30 Hari" value={balances ? formatRupiah(balances.income_30d) : "—"} icon={ArrowDownRight} sub="dari ledger transaksi" accent="forest" />
        <FinanceCard label="Pengeluaran 30 Hari" value={balances ? formatRupiah(balances.expense_30d) : "—"} icon={ArrowUpRight} sub="dari ledger transaksi" accent="amber" />
        <FinanceCard label="Total Simpanan Anggota" value={balances ? formatRupiah(balances.savings_total) : "—"} icon={TrendingUp} sub="pokok + wajib + sukarela" accent="terracotta" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Arus Kas Mingguan</h3>
          <p className="text-xs text-ink/55">4 minggu terakhir · dalam ribu Rupiah</p>
          {!flowLoaded ? (
            <div className="h-72 grid place-items-center text-sm text-ink/55">Memuat…</div>
          ) : !hasCashFlow ? (
            <div className="h-72 grid place-items-center text-center"><div><Inbox className="mx-auto text-ink/30" size={28} /><p className="text-sm text-ink/55 mt-2">Belum ada arus kas</p></div></div>
          ) : (
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <AreaChart data={cashFlow}>
                <defs>
                  <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2D5A27" stopOpacity={0.4} /><stop offset="100%" stopColor="#2D5A27" stopOpacity={0} /></linearGradient>
                  <linearGradient id="g4" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#A94C38" stopOpacity={0.35} /><stop offset="100%" stopColor="#A94C38" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
                <XAxis dataKey="d" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <Tooltip />
                <Area dataKey="masuk" stroke="#2D5A27" fill="url(#g3)" strokeWidth={2.5} />
                <Area dataKey="keluar" stroke="#A94C38" fill="url(#g4)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          )}
        </Card>

        <Card className="rounded-xl border-stone-soft p-6">
          <div className="flex items-center gap-2 mb-1">
            <Banknote className="text-forest" size={17} />
            <h3 className="font-display font-semibold text-ink">Rekonsiliasi Bank</h3>
          </div>
          <p className="text-xs text-ink/55">Saldo dompet & kas wilayah (dari ledger)</p>
          {flowLoaded && (!accountList || accountList.length === 0) ? (
            <div className="py-10 text-center"><Inbox className="mx-auto text-ink/30" size={26} /><p className="text-sm text-ink/55 mt-2">Belum ada saldo</p></div>
          ) : (
          <div className="space-y-2.5 mt-4">
            {(accountList || []).map((b) => (
              <div key={b.bank} className="flex items-center justify-between rounded-lg bg-cream p-3" data-testid={`bank-${b.bank.split(" ")[0].toLowerCase()}`}>
                <div>
                  <div className="font-semibold text-sm text-ink">{b.bank}</div>
                  <div className="text-[11px] text-ink/55">{b.sync}</div>
                </div>
                <div className="font-display font-bold text-sm text-ink">{formatRupiah(b.saldo)}</div>
              </div>
            ))}
          </div>
          )}
        </Card>
      </div>

      <Card className="rounded-xl border-stone-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-ink">Buku Besar (Ledger)</h3>
            <p className="text-xs text-ink/55">Semua transaksi 7 hari terakhir</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg border-stone-soft" onClick={exportAudit} data-testid="finance-audit"><FileBarChart2 size={14} /> Laporan Audit</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="text-[10px] uppercase tracking-wider">ID</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Deskripsi</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Kategori</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Nominal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-ink/55 py-10">Belum ada transaksi.</TableCell></TableRow>
              ) : rows.map((t, i) => (
                <TableRow key={t.id || i} className="border-stone-soft hover:bg-cream/30" data-testid={`finance-row-${t.id}`}>
                  <TableCell className="font-mono text-xs text-ink/55">{t.id}</TableCell>
                  <TableCell className="text-sm">{formatDate(t.date)}</TableCell>
                  <TableCell className="text-sm font-medium text-ink">{t.desc}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 rounded-full text-[10px] uppercase tracking-wider ${
                      t.category === "Penerimaan" ? "bg-sage/20 text-forest hover:bg-sage/20" : "bg-terracotta/15 text-terracotta hover:bg-terracotta/15"
                    }`}>{t.category}</Badge>
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${t.amount > 0 ? "text-forest" : "text-terracotta"}`}>
                    {t.amount > 0 ? "+ " : "- "}{formatRupiah(Math.abs(t.amount))}
                  </TableCell>
                  <TableCell className="text-right text-sm text-ink/75">{t.balance != null ? formatRupiah(t.balance) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const FinanceCard = ({ label, value, icon: Icon, sub, accent = "forest" }) => {
  const accentMap = { forest: "bg-forest/10 text-forest", amber: "bg-amber-warm/15 text-amber-700", terracotta: "bg-terracotta/10 text-terracotta" };
  return (
    <Card className="rounded-xl border-stone-soft p-5">
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${accentMap[accent]}`}><Icon size={17} /></div>
      <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-3">{label}</div>
      <div className="font-display font-bold text-xl text-ink mt-1 truncate">{value}</div>
      <div className="text-[11px] text-ink/55 mt-1">{sub}</div>
    </Card>
  );
};
